import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { createDeviceService } from '../services/deviceService'
import MessagesService from '../services/messagesService'

/**
 * Custom hook to manage device data based on user claims
 */
export function useDevices() {
  const { userClaims } = useAuth()
  const [devices, setDevices] = useState([])
  const [devicesSummary, setDevicesSummary] = useState(null)
  const [recentActivity, setRecentActivity] = useState([])
  const [recentMessages, setRecentMessages] = useState([])
  const [deviceStatuses, setDeviceStatuses] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deviceService, setDeviceService] = useState(null)
  const [messagesService, setMessagesService] = useState(null)

  // Initialize device and messages services when user claims change
  useEffect(() => {
    if (userClaims && userClaims.customer) {
      try {
        const service = createDeviceService(userClaims)
        const msgService = new MessagesService(userClaims.customer)
        setDeviceService(service)
        setMessagesService(msgService)
        setError(null)
      } catch (err) {
        console.error('Error creating services:', err)
        setError(err.message)
        setDeviceService(null)
        setMessagesService(null)
      }
    } else {
      setDeviceService(null)
      setMessagesService(null)
      setDevices([])
      setDevicesSummary(null)
      setRecentActivity([])
      setRecentMessages([])
    }
  }, [userClaims])

  // Load initial data when services are available
  useEffect(() => {
    if (deviceService && messagesService) {
      loadAllData()
    } else {
      setLoading(false)
    }
  }, [deviceService, messagesService])

  const loadAllData = async () => {
    if (!deviceService || !messagesService) return

    setLoading(true)
    setError(null)

    try {
      // Load all data in parallel
      const [allDevices, activity, messages, statuses, legacyIds] = await Promise.all([
        deviceService.getAllDevices(),
        deviceService.getRecentActivity(),
        messagesService.getRecentMessages(500),
        deviceService.getDeviceStatuses(),
        deviceService.getLegacyDeviceIds()
      ])

      setDevices(allDevices)
      setRecentActivity(activity)
      setRecentMessages(messages)
      setDeviceStatuses(statuses)
      
      // Calculate summary across touch + legacy devices (deduplicated by device ID)
      const summary = calculateSummary(allDevices, statuses, legacyIds)
      setDevicesSummary(summary)
    } catch (err) {
      console.error('Error loading device data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const calculateSummary = (deviceList, statusMap, legacyIds = []) => {
    // Build a deduplicated set of all device IDs (touch + legacy)
    const allIdSet = new Set(deviceList.map(d => d.id))
    legacyIds.forEach(id => allIdSet.add(id))
    const allIds = [...allIdSet]

    const summary = {
      total: allIds.length,
      online: 0,
      offline: 0,
      maintenance: 0,
      unknown: 0
    }

    allIds.forEach(id => {
      const status = statusMap[id]
      if (status === 'up' || status === 'online') {
        summary.online++
      } else if (status === 'down' || status === 'offline') {
        summary.offline++
      } else if (status === 'maintenance') {
        summary.maintenance++
      } else {
        summary.unknown++
      }
    })

    return summary
  }

  const refreshData = () => {
    if (deviceService) {
      loadAllData()
    }
  }

  const getDevicesByStatus = async (status) => {
    if (!deviceService) return []
    
    try {
      setError(null)
      return await deviceService.getDevicesByStatus(status)
    } catch (err) {
      console.error('Error fetching devices by status:', err)
      setError(err.message)
      return []
    }
  }

  const getDeviceById = async (deviceId) => {
    if (!deviceService) return null
    
    try {
      setError(null)
      return await deviceService.getDeviceById(deviceId)
    } catch (err) {
      console.error('Error fetching device:', err)
      setError(err.message)
      return null
    }
  }

  return {
    devices,
    devicesSummary,
    recentActivity,
    recentMessages,
    deviceStatuses,
    loading,
    error,
    refreshData,
    getDevicesByStatus,
    getDeviceById,
    customerName: userClaims?.customer || '',
    userPermissions: {
      isAdmin: userClaims?.sadmin || false,
      hasCustomerAccess: userClaims?.cc || false,
      hasLayoutAccess: userClaims?.layout || false
    }
  }
}